import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VoucherModel } from '../models/voucher.model';
import { VoucherService } from '../voucher.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DirtyCheck } from 'src/app/common/guards/form-deactivate.guard';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-voucher-edit',
  standalone: false,
  templateUrl: './voucher-edit.component.html',
  styleUrl: './voucher-edit.component.scss'
})
export class VoucherEditComponent implements OnInit, DirtyCheck {

  title: string = "New Voucher";
  editForm: FormGroup;
  voucherInfo: VoucherModel = {};
  isFormDirty: boolean = false;
  isReady: boolean = false;

  // Voucher type options
  voucherTypeOptions = [
    { value: 1, label: "St. Pierre's" },
    { value: 2, label: "Cash Back" },
    { value: 3, label: "Discount" },
    { value: 4, label: "Gift Card" }
  ];
  permission: any;

  constructor(
    private router: Router,
    private builder: FormBuilder,
    private route: ActivatedRoute,
    private voucherService: VoucherService,
    private snackBarService: SnackBarService,
    private permissionService: PermissionService,
    public dialog: MatDialog,
  ) {

    let voucherId = this.route.snapshot.paramMap.get('id');
    this.editForm = this.builder.group({
      code: this.builder.control(this.voucherInfo.code, [Validators.required, Validators.maxLength(10)]),
      name: this.builder.control(this.voucherInfo.name, [Validators.required, Validators.maxLength(50)]),
      description: this.builder.control(this.voucherInfo.description, [Validators.maxLength(500)]),
      voucher_type: this.builder.control(this.voucherInfo.voucher_type, [Validators.required]),
      value: this.builder.control(this.voucherInfo.value, [Validators.required, Validators.min(0)]),
      is_overridable: this.builder.control(this.convertToBoolean(this.voucherInfo.is_overridable)),
      is_change_payable: this.builder.control(this.convertToBoolean(this.voucherInfo.is_change_payable)),
      account_code: this.builder.control(this.voucherInfo.account_code, [Validators.required, Validators.maxLength(50)]),
      is_valid: this.builder.control(this.convertToBoolean(this.voucherInfo.is_valid, true)), // Default to true
      is_synchable: this.builder.control(this.convertToBoolean(this.voucherInfo.is_synchable, true)) // Default to true
    });

    this.editForm.valueChanges.subscribe(() => {
      this.isFormDirty = true;
    });

    if (voucherId !== null && parseInt(voucherId) !== 0) {
      this.setVoucherInfo(voucherId);
    } else {
      this.isReady = true;
    }

    this.permission = this.permissionService.getRowPermission(this.permissionService.getPagePermission('voucher'), voucherId);
  }

  ngOnInit(): void {
    // Any initialization logic can go here
  }

  /**
   * Convert numeric values (0/1) to boolean for checkboxes
   * @param value - The value to convert (0, 1, true, false, null, undefined)
   * @param defaultValue - Default boolean value if input is null/undefined
   * @returns boolean value
   */
  convertToBoolean(value: any, defaultValue: boolean = false): boolean {
    if (value === null || value === undefined) {
      return defaultValue;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      return value === '1' || value.toLowerCase() === 'true';
    }
    return defaultValue;
  }

  /**
   * Convert boolean values to numeric (0/1) for API
   * @param value - The boolean value to convert
   * @returns 0 or 1
   */
  convertToNumeric(value: boolean): number {
    return value ? 1 : 0;
  }

  /**
   * Dirtyness check
   * @returns 
   */
  isDirty(): boolean {
    return this.isFormDirty;
  }

  /**
   * Navigate back to voucher list
   */
  navBack(): void {
    this.router.navigate(['/promotion/voucher']);
  }

  /**
   * If voucher id is null set an empty voucher info
   * if voucher id is not null call api to retrieve the info
   * @param voucherId 
   */
  setVoucherInfo(voucherId: any): void {
    this.isReady = false; // Disable button while loading
    let response = this.voucherService.getVoucherById(voucherId).subscribe({
      next: (response) => {
        if (response.status == 'SUCCESS') {
          this.voucherInfo = response.data.voucher;

          // Convert numeric values to boolean for form display
          const formData = {
            ...this.voucherInfo,
            is_overridable: this.convertToBoolean(this.voucherInfo.is_overridable),
            is_change_payable: this.convertToBoolean(this.voucherInfo.is_change_payable),
            is_valid: this.convertToBoolean(this.voucherInfo.is_valid, true),
            is_synchable: this.convertToBoolean(this.voucherInfo.is_synchable, true)
          };

          this.editForm.patchValue(formData);
          this.title = this.getTitle();
          this.isFormDirty = false;
          this.isReady = true; // Enable button after successful load
        } else {
          let snackBarData: SnackBarData = {
            message: response.message,
            title: 'Failed!!!',
            type: 'error'
          }
          this.snackBarService.openSnackBar(snackBarData);
          this.isReady = true; // Enable button even on failure to allow retry
        }
      },
      error: (error) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to get data from server. Connection to the server failed.',
          title: 'Failed!!!',
          type: 'error'
        }
        this.snackBarService.openSnackBar(snackBarData);
        this.isReady = true; // Enable button even on error to allow retry
      }
    });
  }

  /**
   * @returns the title for the dialog
   * since the same dialog is used for edit and new, dynamically decide the title
   */
  getTitle(): string {
    let title = (this.voucherInfo.id == 0) ? "New Voucher" : "Editing : " + this.voucherInfo.name;
    return title;
  }

  /**
   * Get voucher type label for display
   */
  getVoucherTypeLabel(value: number): string {
    const option = this.voucherTypeOptions.find(opt => opt.value === value);
    return option ? option.label : value?.toString() || '';
  }

  /**
   * On save
   * if the details are valid call the api to update the changes.
   */
  save(): void {
    if (this.editForm.valid) {
      this.isReady = false; // Disable button during save
      this.editForm.markAllAsTouched();

      // Get form values and convert boolean fields to numeric (0/1)
      const formValues = this.editForm.value;
      const voucherData = {
        ...this.voucherInfo,
        ...formValues,
        is_overridable: this.convertToNumeric(formValues.is_overridable),
        is_change_payable: this.convertToNumeric(formValues.is_change_payable),
        is_valid: this.convertToNumeric(formValues.is_valid),
        is_synchable: this.convertToNumeric(formValues.is_synchable)
      };

      this.voucherService.update(voucherData).subscribe({
        next: (response) => {
          if (response.status == 'SUCCESS') {
            // handle success
            this.isFormDirty = false;
            let snckBarData: SnackBarData = {
              type: 'success',
              title: 'Saved',
              message: 'The record for ' + voucherData.name + " has been updated successfully."
            }
            this.snackBarService.openSnackBar(snckBarData);
            this.isReady = true; // Re-enable button after success
            this.router.navigate(['/promotion/voucher']);
          } else {
            let snackBarData: SnackBarData = {
              message: response.message,
              title: 'Failed!!!',
              type: 'error'
            }
            this.snackBarService.openSnackBar(snackBarData);
            this.isReady = true; // Re-enable button after error
          }
        },
        error: (error) => {
          // handle error
          let snackBarData: SnackBarData = {
            message: 'Failed to update. Connection to the server failed',
            title: 'Failed!!!',
            type: 'error'
          }
          this.snackBarService.openSnackBar(snackBarData);
          this.isReady = true; // Re-enable button after error
        }
      });
    } else {
      this.editForm.markAllAsTouched();
      let snackBarData: SnackBarData = {
        message: 'Please fill all required fields correctly.',
        title: 'Validation Error',
        type: 'error'
      }
      this.snackBarService.openSnackBar(snackBarData);
    }
  }

  /**
   * Delete voucher
   */
  delete(): void {
    if (this.voucherInfo.id && this.voucherInfo.id > 0) {
      const message = `Are you sure you want to delete the voucher "${this.voucherInfo.name}"?`;
      const dialogData = new ConfirmDialogModel("Confirm Deletion", message);

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: "400px",
        data: dialogData
      });

      dialogRef.afterClosed().subscribe(dialogResult => {
        if (dialogResult) {
          this.voucherService.delete(this.voucherInfo).subscribe({
            next: (response) => {
              if (response.status == 'SUCCESS') {
                let snackBarData: SnackBarData = {
                  type: 'success',
                  title: 'Deleted',
                  message: 'The voucher "' + this.voucherInfo.name + '" has been deleted successfully.'
                }
                this.snackBarService.openSnackBar(snackBarData);
                this.router.navigate(['/promotion/voucher']);
              } else {
                let snackBarData: SnackBarData = {
                  message: response.message,
                  title: 'Failed!!!',
                  type: 'error'
                }
                this.snackBarService.openSnackBar(snackBarData);
              }
            },
            error: (error) => {
              let snackBarData: SnackBarData = {
                message: 'Failed to delete. Connection to the server failed',
                title: 'Failed!!!',
                type: 'error'
              }
              this.snackBarService.openSnackBar(snackBarData);
            }
          });
        }
      });
    }
  }
}
